/**
 * @description Component-OrderBook
 */
import './index.less';

import { CHAIN_ID } from '@derivation-tech/context';
import classNames from 'classnames';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { EmptyDataWrap } from '@/components/EmptyDataWrap';
import { useMediaQueryDevice } from '@/components/MediaQuery';
import { TRADE_TYPE } from '@/constants/trade';
import { WrappedBigNumber, WrappedBigNumberLike } from '@/entities/WrappedBigNumber';
import { WrappedOrder } from '@/entities/WrappedOrder';
import { WrappedPair } from '@/entities/WrappedPair';
import { useWrappedOrderList } from '@/features/account/orderHook';
import {
  useCurrentPairByDevice,
  useGetAlignedFairPrice,
  usePairAdjustedRange,
  usePairDisabledForLimit,
} from '@/features/pair/hook';
import { useTradeSide } from '@/features/trade/hooks';
import { useTradeStore } from '@/features/trade/store';
import { useAppDispatch } from '@/hooks';
import { useUserAddr } from '@/hooks/web3/useChain';
import { formatNumber } from '@/utils/numberUtil';

import IconArrowFall from '@/assets/svg/icon_arrow_fall_16.svg?react';
import IconArrowRise from '@/assets/svg/icon_arrow_rise_16.svg?react';
import { useMockDevTool } from '@/components/Mock';
import {
  useFuturesOrderBookWithStepRatio,
  useIsFetchedFuturesOrderBook,
  useWatchOrderbookChange,
} from '@/features/futures/hooks';
import { IExtendDepthChartData } from '@/types/chart';
import { IFuturesOrderBookItem } from '@/types/futures';

import { WRAPPED_ZERO } from '@/constants/bigNumber';
import { PAIR_PAGE_TYPE } from '@/constants/global';
import { setTradeFormSide, setTradeFormType } from '@/features/trade/actions';
import { useSideNavigate } from '@/hooks/useRouterNavigate';
import { ORDER_SPACING, Side } from '@synfutures/sdks-perp';
import { usePrevious } from 'ahooks';
import _ from 'lodash';
import OrderBookSkeleton from './OrderBookSkeleton';

// let depthPolling: NodeJS.Timeout | undefined = undefined;

interface IPropTypes {
  children?: React.ReactNode;
  className?: string;
  pair: WrappedPair | undefined;
  chainId: CHAIN_ID | undefined;
  stepRatio?: number;
}

const OrderBook: FC<IPropTypes> = function ({ pair, chainId, stepRatio = ORDER_SPACING }) {
  const depthDataFromApi = useFuturesOrderBookWithStepRatio(chainId, pair?.instrumentAddr, pair?.expiry, stepRatio);
  const isFetched = useIsFetchedFuturesOrderBook(chainId, pair?.instrumentAddr, pair?.expiry, stepRatio);

  const dispatch = useAppDispatch();
  const userAddr = useUserAddr();

  const currentPair = useCurrentPairByDevice(chainId);

  const prevPrice = usePrevious(currentPair?.wrapAttribute('fairPrice')?.toNumber());
  const { orderbookUnitBase, setOrderBookUnitBase, isTurnOnCrossMarket, setLimitPrice, setAlignedLimitPrice, setTick } =
    useTradeStore();

  const { pairDisabledInLimit } = usePairDisabledForLimit(currentPair);
  const tradeSide = useTradeSide(chainId);
  const orders = useWrappedOrderList(chainId, userAddr, pair?.id);
  const [adjustedLowerPrice, adjustedUpperPrice] = usePairAdjustedRange(currentPair, tradeSide, isTurnOnCrossMarket);
  const { deviceType } = useMediaQueryDevice();

  const { t } = useTranslation();
  const getAlignedFairPrice = useGetAlignedFairPrice(currentPair);
  const { isMockSkeleton } = useMockDevTool();
  const { pairPageNavigate } = useSideNavigate(chainId);
  const { setShowTradeAreaOverlay, setFromOrderBook } = useTradeStore();
  const setLimitPriceToTrade = useCallback(
    async (price: WrappedBigNumberLike, side: Side, tick?: number) => {
      if (pairDisabledInLimit) {
        return;
      }
      if (currentPair) {
        pairPageNavigate(currentPair?.symbol || '', PAIR_PAGE_TYPE.TRADE, undefined, {
          state: { toLimitTab: true },
        });
        setShowTradeAreaOverlay(undefined);
        setFromOrderBook(true);
      }
      gtag('event', 'adjust_price', {
        adjust_price: 'click_order_book', //plus_or_mins, enter, click_order_book
      });
      if (currentPair) {
        const alignedFairPrice = getAlignedFairPrice(side);

        let priceFinal = WrappedBigNumber.from(price);
        const adjustTradeSide = side;
        if (chainId) {
          if (adjustTradeSide === Side.LONG) {
            if (adjustedLowerPrice && priceFinal.lt(adjustedLowerPrice)) {
              priceFinal = adjustedLowerPrice;
            }
            if (alignedFairPrice && priceFinal.gt(alignedFairPrice)) {
              priceFinal = alignedFairPrice;
            }
          }
          if (adjustTradeSide === Side.SHORT) {
            if (adjustedUpperPrice && priceFinal.gt(adjustedUpperPrice || 0)) {
              priceFinal = adjustedUpperPrice;
            }
            if (alignedFairPrice && alignedFairPrice.gt(priceFinal)) {
              priceFinal = alignedFairPrice;
            }
          }
          const checkedPrice = formatNumber(priceFinal.stringValue, currentPair?.priceDecimal, false);

          setLimitPrice(checkedPrice);
          setAlignedLimitPrice(checkedPrice);
          if (tick) setTick(tick);

          dispatch(
            setTradeFormSide({
              chainId,
              tradeSide: isTurnOnCrossMarket ? tradeSide : side,
            }),
          );

          dispatch(
            setTradeFormType({
              chainId,
              tradeType: TRADE_TYPE.LIMIT,
            }),
          );
        }
      }
    },
    [
      adjustedLowerPrice,
      adjustedUpperPrice,
      chainId,
      currentPair,
      dispatch,
      getAlignedFairPrice,
      isTurnOnCrossMarket,
      pairDisabledInLimit,
      pairPageNavigate,
      setAlignedLimitPrice,
      setFromOrderBook,
      setLimitPrice,
      setShowTradeAreaOverlay,
      setTick,
      tradeSide,
    ],
  );
  useEffect(() => {
    setOrderBookUnitBase(true);
  }, [pair?.id]);

  useEffect(() => {
    setOrderBookUnitBase(
      pair?.isInverse ? !!pair?.wrapAttribute('fairPrice').gt(1) : !!pair?.wrapAttribute('fairPrice').lt(1),
    );
  }, [pair?.id]);

  const findClosest = useCallback(
    (array: (IFuturesOrderBookItem & { isShowIcon?: boolean })[], order: WrappedOrder, side: Side) => {
      if (side === Side.LONG) {
        const orderedArr = array;

        const best = orderedArr.find((item) =>
          order.isInverse
            ? WrappedBigNumber.from(item.tick).gte(order.tick)
            : WrappedBigNumber.from(item.tick).lte(order.tick),
        );
        return best;
      } else if (side === Side.SHORT) {
        const orderedArr = array;
        const best = _.clone(orderedArr)
          .reverse()
          .find((item) =>
            order.isInverse
              ? WrappedBigNumber.from(item.tick).lte(order.tick)
              : WrappedBigNumber.from(item.tick).gte(order.tick),
          );

        return best;
      }
    },
    [],
  );

  const sellDataWithOrder = useMemo(() => {
    // data must be mapped to become immutable
    const data: (IFuturesOrderBookItem & { isShowIcon?: boolean })[] | undefined = depthDataFromApi?.right.map((d) => ({
      ...d,
    }));
    if (data && orders) {
      const sellOrders = orders.filter((order) => order.side === Side.SHORT);
      sellOrders.forEach((order) => {
        const closest = findClosest(data, order, order.side);
        if (closest) {
          //console.log('🚀 ~ sellOrders.forEach ~ closest:', { closest, order, sellOrders, orders, depthDataFromApi });
          closest.isShowIcon = true;
        }
      });
    }
    return data?.slice(0, 10);
  }, [depthDataFromApi?.right, findClosest, orders]);

  const buyDataWithOrder = useMemo(() => {
    const data: (IFuturesOrderBookItem & { isShowIcon?: boolean })[] | undefined = depthDataFromApi?.left.map((d) => ({
      ...d,
    }));
    if (data && orders) {
      const buyOrders = orders.filter((order) => order.side === Side.LONG);
      buyOrders.forEach((order) => {
        const closest = findClosest(data, order, order.side);
        if (closest) {
          closest.isShowIcon = true;
        }
      });
    }
    return data?.slice(0, 10);
  }, [depthDataFromApi?.left, findClosest, orders]);

  const sellData = useMemo(() => {
    const data = sellDataWithOrder;
    if (data && pair) {
      // Use the pre-calculated values from the API
      return data.map((item) => ({
        ...item,
        sum: item.baseSum?.toNumber() || 0,
        quote: item.quoteSize?.toNumber() || 0,
        sumQuote: item.quoteSum?.toNumber() || 0,
      }));
    }
    return [
      {
        tick: 0,
        price: WRAPPED_ZERO,
        isShowIcon: false,
        base: WRAPPED_ZERO,
        quoteSize: WRAPPED_ZERO,
        baseSum: WRAPPED_ZERO,
        quoteSum: WRAPPED_ZERO,
      },
    ] as IExtendDepthChartData[];
  }, [sellDataWithOrder, pair]);

  const buyData: IExtendDepthChartData[] = useMemo(() => {
    const data = buyDataWithOrder;
    if (data && pair) {
      // Use the pre-calculated values from the API
      return data.map((item) => ({
        ...item,
        sum: item.baseSum?.toNumber() || 0,
        quote: item.quoteSize?.toNumber() || 0,
        sumQuote: item.quoteSum?.toNumber() || 0,
      }));
    }

    return new Array(10).fill({
      tick: 0,
      price: WRAPPED_ZERO,
      isShowIcon: false,
      base: WRAPPED_ZERO,
      quoteSize: WRAPPED_ZERO,
      baseSum: WRAPPED_ZERO,
      quoteSum: WRAPPED_ZERO,
    }) as IExtendDepthChartData[];
  }, [buyDataWithOrder, pair]);
  const maxSum = useMemo(() => {
    return Math.max(
      ...sellData.map((item) => (orderbookUnitBase ? item.baseSum?.toNumber() || 0 : item.quoteSum?.toNumber() || 0)),
      ...buyData.map((item) => (orderbookUnitBase ? item.baseSum?.toNumber() || 0 : item.quoteSum?.toNumber() || 0)),
    );
  }, [buyData, orderbookUnitBase, sellData]);

  const getWidth = (sum: number, max: number) => {
    return `calc(${WrappedBigNumber.from(sum).div(max).mul(100).stringValue}%)`;
  };
  const loading = isMockSkeleton || !isFetched;

  useWatchOrderbookChange(chainId, currentPair?.instrumentAddr, currentPair?.expiry);

  return (
    <div className={classNames('syn-order-book', deviceType)}>
      <div className="syn-order-book-head">
        <div className="syn-order-book-item">
          <div className="syn-order-book-item_price">{t('common.orderBook.price')}</div>
          <div className="syn-order-book-item_size">
            {t('common.orderBook.size')}(
            {orderbookUnitBase ? pair?.rootInstrument.baseToken.symbol : pair?.rootInstrument.quoteToken.symbol})
          </div>
          <div className="syn-order-book-item_sum">{t('common.orderBook.sum')}</div>
        </div>
      </div>

      <OrderBookSkeleton isLoading={loading}>
        <div className="syn-order-book-body">
          <div className="syn-order-book-table syn-order-book-table_sell">
            {sellData.map((sell, i) => (
              <div
                className="syn-order-book-item"
                onClick={() => setLimitPriceToTrade(sell.price, Side.SHORT, sell.tick)}
                key={sell.tick?.toString() + sell.price.toString() + i}>
                <div className="syn-order-book-item_price">
                  <EmptyDataWrap isLoading={!sell.price}>
                    {WrappedBigNumber.from(sell.price).formatPriceNumberWithTooltip()}
                  </EmptyDataWrap>
                </div>
                <div className="syn-order-book-item_size">
                  <EmptyDataWrap isLoading={!sell?.base}>
                    {WrappedBigNumber.from(
                      orderbookUnitBase ? sell?.base?.toNumber() || 0 : sell.quoteSize?.toNumber() || 0,
                    ).formatNumberWithTooltip({
                      isShowTBMK: true,
                    })}
                  </EmptyDataWrap>
                </div>

                <div className="syn-order-book-item_sum">
                  <EmptyDataWrap isLoading={!sell.baseSum}>
                    {WrappedBigNumber.from(
                      orderbookUnitBase ? sell.baseSum?.toNumber() || 0 : sell.quoteSum?.toNumber() || 0,
                    ).formatNumberWithTooltip({
                      isShowTBMK: true,
                    })}
                  </EmptyDataWrap>
                </div>

                <div className="syn-order-book-item-bg-wrap">
                  <div
                    className="syn-order-book-item-bg"
                    style={{
                      width: getWidth(
                        orderbookUnitBase ? sell.baseSum?.toNumber() || 0 : sell.quoteSum?.toNumber() || 0,
                        maxSum,
                      ),
                    }}></div>
                </div>
                {sell.isShowIcon && (
                  <div className={classNames('syn-order-book-item-ordered', 'syn-order-book-item-ordered_sell')}></div>
                )}
              </div>
            ))}
          </div>
          <div className="syn-order-book-price-wrap">
            <div
              className={classNames(
                'syn-order-book-price',
                pair?.wrapAttribute('fairPrice')?.gte(prevPrice || 0) ? 'text-success' : 'text-danger',
              )}>
              <span>{pair?.wrapAttribute('fairPrice').formatPriceNumberWithTooltip()}</span>

              {pair?.wrapAttribute('fairPrice')?.gte(prevPrice || 0) ? <IconArrowRise /> : <IconArrowFall />}
            </div>

            <div className="syn-order-book-price-secondary">
              {pair?.wrapAttribute('markPrice').formatPriceNumberWithTooltip()}
            </div>
          </div>
          <div className="syn-order-book-table syn-order-book-table_buy">
            {buyData.map((buy, i) => (
              <div
                onClick={() => setLimitPriceToTrade(buy.price, Side.LONG, buy.tick)}
                className="syn-order-book-item"
                key={buy.tick?.toString() + buy.price.toString() + i}>
                <div className="syn-order-book-item_price">
                  <EmptyDataWrap isLoading={!buy.price}>
                    {WrappedBigNumber.from(buy.price).formatPriceNumberWithTooltip()}
                  </EmptyDataWrap>
                </div>
                <div className="syn-order-book-item_size">
                  {
                    <EmptyDataWrap isLoading={!buy.base}>
                      {WrappedBigNumber.from(
                        orderbookUnitBase ? buy.base?.toNumber() || 0 : buy.quoteSize?.toNumber() || 0,
                      ).formatNumberWithTooltip({
                        isShowTBMK: true,
                      })}
                    </EmptyDataWrap>
                  }
                </div>

                <div className="syn-order-book-item_sum">
                  {
                    <EmptyDataWrap isLoading={!buy.baseSum}>
                      {WrappedBigNumber.from(
                        orderbookUnitBase ? buy.baseSum?.toNumber() || 0 : buy.quoteSum?.toNumber() || 0,
                      ).formatNumberWithTooltip({
                        isShowTBMK: true,
                      })}
                    </EmptyDataWrap>
                  }
                </div>

                <div className="syn-order-book-item-bg-wrap">
                  <div
                    className="syn-order-book-item-bg"
                    style={{
                      width: getWidth(
                        orderbookUnitBase ? buy.baseSum?.toNumber() || 0 : buy.quoteSum?.toNumber() || 0,
                        maxSum,
                      ),
                    }}></div>
                </div>
                {buy.isShowIcon && (
                  <div className={classNames('syn-order-book-item-ordered', 'syn-order-book-item-ordered_buy')}></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </OrderBookSkeleton>
    </div>
  );
};

export default OrderBook;
